"use client";

import Link from "next/link";
import { Menu, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";

// Definiujemy bazowe linki, które są zawsze widoczne
const baseLinks = [
  { name: "Oferta", href: "#offer" },
  { name: "Opinie", href: "#testimonials" }, 
  { name: "Zespół", href: "#cooperate" },
  { name: "Kontakt", href: "#contact", isCTA: true },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Sprawdzamy środowisko wewnątrz komponentu
  const isProd = process.env.NODE_ENV === 'production';

  // Dynamicznie budujemy listę linków
  // Jeśli nie jest to produkcja, dodajemy /work na początek listy
  const navLinks = [
    ...(isProd ? [] : [{ name: "Projekty i case studies", href: "/work" }]),
    ...baseLinks
  ];

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-primary/10">
      <div className="mx-auto max-w-6xl lg:px-20 px-7">
        <div className="flex h-16 items-center justify-between">
          
          <Link 
            href="/" 
            scroll={false}
            className="font-bold text-lg tracking-tight hover:opacity-80 transition-opacity" 
            onClick={(e) => {
              setIsOpen(false);
              if (window.location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            Bartosz Jawor UX/UI
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link 
                key={link.name} 
                href={link.href} 
                className={
                  link.isCTA 
                    ? "bg-primary text-white px-5 py-2 rounded-full text-sm font-semibold hover:bg-primary/90 transition-all shadow-md hover:shadow-lg active:scale-95" 
                    : "text-m font-medium text-primary/80 hover:text-primary transition-colors"
                }
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Mobile Button */}
          <button 
            className="lg:hidden p-2 text-primary focus:outline-none focus:ring-2 focus:ring-primary/20 rounded-md"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Zamknij menu" : "Otwórz menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div 
          id="mobile-navigation"
          ref={menuRef} 
          role="dialog"
          aria-modal="true"
          aria-label="Nawigacja mobilna"
          className="lg:hidden absolute top-16 left-0 right-0 bg-white border-b border-primary/20 p-6 flex flex-col gap-3 animate-in fade-in slide-in-from-top-5 shadow-2xl"
        >
          {navLinks.map((link) => (
            <Link 
              key={link.name} 
              href={link.href} 
              onClick={() => setIsOpen(false)}
              className={
                link.isCTA 
                  ? "bg-primary text-white text-center py-3 rounded-xl font-bold mt-2 shadow-lg" 
                  : "text-lg font-medium py-3 border-b border-primary/5 last:border-0"
              }
            >
              {link.name}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}